/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * What size to upload, said BEFORE the upload (AGL-2486).
 *
 * Every upload surface states a recommended size before a file is picked.
 * Reporting the dimensions of the picture already chosen is a different
 * sentence entirely, and it arrives too late to change anything.
 *
 * Every number here is a REQUIREMENT SOMEBODY ELSE PUBLISHES, not a taste
 * call, and each says whose. That is the difference between guidance an
 * author can act on and a number invented to fill a caption:
 *
 * - a social card is 1200×630 because that is the Open Graph aspect every
 *   major network crops to;
 * - a favicon is square because every consumer of it is;
 * - a publisher logo has a floor because Google's structured-data guidance
 *   sets one, and a logo under it is dropped from rich results rather than
 *   scaled up.
 *
 * Stated as ADVICE, never as a gate. Nothing here rejects an upload: an
 * author who knows their case better than the caption does is not stopped,
 * and a smaller image renders — it just may not be used everywhere.
 */

export interface MediaSizeHint {
  /** The one-line caption shown under the control. */
  text: string
}

/** Open Graph's card, and the aspect every network crops to. */
export const SOCIAL_IMAGE_HINT =
  'Recommended: 1200×630 PNG or JPG — the size social networks crop to.'

/**
 * Browsers ask for many favicon sizes, and the site generates every one of
 * them from this single file (AGL-3484): 16, 32 and 48px PNGs, a multi-size
 * `/favicon.ico` and the touch icons. 512 is the largest anything asks for,
 * so it is the one that scales down to all of them without being upscaled.
 * An `.ico` is linked as it is — nothing can redraw it — so the hint names
 * the formats that get the generated set.
 */
export const FAVICON_HINT =
  'Recommended: one square PNG, SVG or JPG, 512×512 or larger — every tab, ' +
  'bookmark and home-screen size is generated for you.'

/**
 * The icon an installed site shows on a home screen.
 *
 * Every size an installer asks for, 48 to 512px, and the maskable tiles
 * Android crops into its own shapes are generated from this one file
 * (AGL-3484), so a single 512×512 upload covers the whole set. Square
 * because a wider file is letterboxed to fit, and a wordmark shrunk into a
 * tile loses its own name.
 */
export const APP_ICON_HINT =
  'Recommended: one square PNG or SVG, 512×512 or larger — a mark rather ' +
  'than a wordmark. Every home-screen and install size is generated for you.'

/**
 * Google's structured-data guidance sets a floor for a publisher logo, and a
 * logo under it is dropped from rich results rather than scaled up — which is
 * the failure worth warning about, because nothing in the console can see it
 * happen.
 */
export const ENTITY_LOGO_HINT =
  'Recommended: at least 112×112, PNG or SVG — search engines skip a ' +
  'publisher logo smaller than that rather than scaling it up.'

/**
 * The spacing a logo looks right with everywhere it is shown (AGL-4119): tiles
 * and rails draw the picture edge to edge, so the margin has to be its own.
 * Words the founder approved. Every logo upload says them.
 */
export const LOGO_SPACING_HINT =
  'Square, at least 512 px, the logo filling about 80% with a little space around it.'

/** A site's own mark, rendered at whatever height the layout gives it. */
export const SITE_LOGO_HINT =
  `${LOGO_SPACING_HINT} SVG or PNG keeps it sharp at every size.`

/**
 * A workspace's mark, which the native apps draw too. They cannot draw an SVG,
 * so this one steers to the two formats every surface can show.
 */
export const WORKSPACE_LOGO_HINT =
  `${LOGO_SPACING_HINT} Use a PNG or JPEG so it shows in the apps too.`

/** A byline avatar: square, small on the page, crisp on retina. */
export const AVATAR_HINT =
  'Recommended: a square PNG or JPG, at least 400×400 — it is shown small, ' +
  'but on retina screens at twice the size.'

/** An entry's cover doubles as its share card, so it takes that shape. */
export const COVER_IMAGE_HINT =
  'Recommended: 1200×630 PNG or JPG — it heads the entry and doubles as its ' +
  'share card.'

/**
 * An entry's featured video (AGL-2954), whose captured frame can also become
 * its cover.
 *
 * MP4, because H.264 video in an MP4 container plays in every major browser
 * without a fallback. 1920×1080, because the frame the library captures at
 * upload is kept up to 1920 pixels wide, so a film that size gives a cover
 * that fills the 1200×630 share card without being scaled up.
 */
export const COVER_VIDEO_HINT =
  'Recommended: an MP4 at 1920×1080 — every major browser plays MP4, and a ' +
  'frame that size makes a sharp cover.'

/**
 * An email logo, which is the one that must NOT be an SVG.
 *
 * Most email clients refuse SVG outright and none of them resize an image the
 * way a browser does — a logo arrives at the width it was authored at, on a
 * screen that may be twice that density. So the advice here is the opposite of
 * {@link SITE_LOGO_HINT}'s, and saying "same as your site logo" would be
 * wrong in both directions.
 */
export const EMAIL_LOGO_HINT =
  'Recommended: a PNG about 400px wide — email clients do not resize images, ' +
  'and most refuse SVG.'

/**
 * A PERSON's picture, which is not a logo and does not take a logo's advice.
 *
 * `schema.org` gives `logo` to an Organization and `image` to a Person, and
 * Google's guidance for the two is different in kind: a publisher logo has a
 * small floor because it is drawn as a mark, and a person's image is a
 * photograph shown at whatever size the result gives it. Recommending
 * 112×112 for a face would be recommending a thumbnail.
 */
export const PERSON_IMAGE_HINT =
  'Recommended: a square photo, at least 800×800 — it is shown as a picture ' +
  'of a person rather than a mark, so it wants the resolution.'
